import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { AppDispatch } from 'config/redux'
import { RootState, Task } from 'constants/interfaces'
import { getTasksInsights, setTasksDeepDiveInsights } from 'redux/actions/tasks_v2'
import { buildContextRows, ContextRow } from 'components/NewTaskDashboard/buildContextRows'

// Drives the dashboard's get-task-insights drill-down for a single owner task.
// Owner tasks come from the `getTasks` endpoint and aren't guaranteed to live in
// `state.tasks_v2.insights`, so the recipient drill-down is fed the task's own
// groups/users/tags/is_individual_results directly.
export const useOwnerTaskInsights = (task: Task | null) => {
  const dispatch: AppDispatch = useDispatch()

  const deepDiveInsights = useSelector((state: RootState) => state.tasks_v2.deepDiveInsights)
  const { groups: groupsObject, retailUsersObject } = useSelector((state: RootState) => state.config.config)

  const [isLoading, setIsLoading] = useState(Boolean(task?.tid))
  const [hasLoaded, setHasLoaded] = useState(false)

  const tid = task?.tid ?? null
  const activeRecurrenceTs = task?.is_recurring ? task.recurrence_ts || null : null

  useEffect(() => {
    if (!tid) return

    let isMounted = true
    setIsLoading(true)
    setHasLoaded(false)
    dispatch(setTasksDeepDiveInsights({ contexts: {}, insights: {} }))

    const load = async () => {
      await dispatch(
        getTasksInsights({
          tid,
          recurrence_ts: activeRecurrenceTs,
          recipients: task
            ? {
                groups: task.groups,
                users: task.users,
                tags: task.tags,
                is_individual_results: task.is_individual_results,
              }
            : undefined,
        })
      )
      if (isMounted) {
        setIsLoading(false)
        setHasLoaded(true)
      }
    }

    load()
    return () => {
      isMounted = false
    }
    // `task` reference is stable per tid here; recipients are derived from the same task.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, tid, activeRecurrenceTs])

  const contexts = useMemo<ContextRow[]>(
    () =>
      buildContextRows(deepDiveInsights, {
        groupsObject,
        retailUsersObject,
        activeRecurrenceTs,
        // Owner tasks have no insights `results` array, so AI score deductions are omitted.
        results: [],
      }),
    [deepDiveInsights, groupsObject, retailUsersObject, activeRecurrenceTs]
  )

  // The get-task-insights response can be empty ({ statuses: {}, insights: {} }) for a
  // short while right after a task is created, before the backend computes its insights.
  // Surface that so the view can prompt the user to check back shortly.
  const isAwaitingInsights = hasLoaded && contexts.length === 0

  return { contexts, isLoading, isAwaitingInsights, activeRecurrenceTs }
}
